import React, { useEffect, useMemo, useState, useCallback, useContext } from 'react';
import { api } from '../api';
import CompanyDetailPanel from './CompanyDetailPanel';
import { AuthContext } from './AuthGate';

/**
 * CompaniesListPanel — all companies, sortable, with bucket + industry filtering
 * and full detail side panel.
 */

const SORT_OPTIONS = [
  { id: 'warmth', label: 'Warmth' },
  { id: 'icp', label: 'ICP Score' },
  { id: 'connection', label: 'Connection' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'name', label: 'Name' },
];

// Hover tooltip for filter chips — shows the bucket's definition so reps
// know what each filter means (punch list: "Whale" needed a definition).
function ChipTooltip({ text, children }) {
  if (!text) return children;
  return (
    <span className="relative group inline-flex">
      {children}
      <span className="pointer-events-none absolute left-0 top-full mt-1.5 z-50 hidden group-hover:block w-64 bg-slate-800 border border-slate-600 rounded-lg shadow-xl px-3 py-2 text-[11px] text-slate-200 leading-relaxed whitespace-normal text-left">
        {text}
      </span>
    </span>
  );
}

function warmthBreakdown(company) {
  // Server now sends the structured breakdown — render it directly so the
  // numbers match what was actually used. Falls back to a derived
  // breakdown for any old cached payload that doesn't include it.
  const b = company.warmth_breakdown;
  const parts = [];
  if (b) {
    if (b.connection_score > 0) {
      parts.push(`Connections (title-weighted) = ${b.connection_score}`);
      if (b.top_titles && b.top_titles.length) {
        for (const [t, s] of b.top_titles) {
          parts.push(`  · ${t || 'unknown'}: +${s}`);
        }
      }
    }
    if (b.follower_score > 0)   parts.push(`Followers = ${b.follower_score}`);
    if (b.engagement_score > 0) parts.push(`Engagement = ${b.engagement_score}`);
    if (b.intro_bonus > 0)      parts.push(`Multi-intro bonus = ${b.intro_bonus}`);
    if (b.research_bonus > 0)   parts.push(`Research bonus = ${b.research_bonus}`);
    parts.push(`Raw = ${b.raw}`);
    if (b.capped_at_100) parts.push(`Capped at 100`);
    parts.push(`Warmth = ${company.warmth || 0}`);
    return parts.join('\n');
  }
  // legacy fallback
  const conn = company.connection_count || 0;
  const follow = company.follower_count || 0;
  const engage = company.engagement_count || 0;
  if (conn > 0)   parts.push(`${conn} connection${conn > 1 ? 's' : ''}`);
  if (follow > 0) parts.push(`${follow} follower${follow > 1 ? 's' : ''}`);
  if (engage > 0) parts.push(`${engage} engagement${engage > 1 ? 's' : ''}`);
  parts.push(`Warmth = ${company.warmth || 0}`);
  return parts.join('\n');
}

export default function CompaniesListPanel() {
  const auth = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  // Default view = the Prospects list (companies not yet sorted into a
  // bucket) so reps land on their working queue, not the master list.
  const [selectedBuckets, setSelectedBuckets] = useState(() => new Set(['__none__']));
  const [selectedIndustries, setSelectedIndustries] = useState(new Set());
  const [sortBy, setSortBy] = useState('icp');   // default: ICP (research_score) descending — punch list
  const [syncing, setSyncing] = useState(false);
  const [bucketDefs, setBucketDefs] = useState([]);
  const [showIndustryFilter, setShowIndustryFilter] = useState(false);

  // Multi-select state
  const [checkedIds, setCheckedIds] = useState(new Set());

  // Assignee distribution: list of active users + filter mode + saving flag.
  // ownerFilter: 'all' | 'mine' | 'unassigned' | <specific user_id>
  const [users, setUsers]           = useState([]);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [assignSaving, setAssignSaving] = useState(false);

  useEffect(() => {
    api.listUsers()
      .then(rs => setUsers((rs || []).filter(u => u.active)))
      .catch(() => setUsers([]));
  }, []);

  // Bulk-assign N selected companies to a user. Pass null to clear.
  const assignOwnerToChecked = async (userId) => {
    if (!checkedIds.size) return;
    setAssignSaving(true);
    try {
      await api.bulkAssignCompanies([...checkedIds], userId);
      setCheckedIds(new Set());
      await load(1);
    } catch (err) {
      console.error('Bulk assign failed:', err);
    } finally {
      setAssignSaving(false);
    }
  };

  // Side panel state
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [warmthPopoverId, setWarmthPopoverId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailResearch, setDetailResearch] = useState(null);

  const load = useCallback(async (resync = 0) => {
    try {
      const data = await api.getCompanies(resync);
      setCompanies(data || []);
      setLoadError('');
    } catch (err) {
      console.error('Failed to load companies:', err);
      setLoadError(err.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getBucketDefinitions().then(d => setBucketDefs(d || [])).catch(() => {});
  }, []);

  // Load company detail when selected
  useEffect(() => {
    if (!selectedCompanyId) {
      setDetailData(null);
      setDetailResearch(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    Promise.all([
      api.getCompany(selectedCompanyId),
      api.getCompanyResearch(selectedCompanyId).catch(() => null),
    ]).then(([company, research]) => {
      if (cancelled) return;
      setDetailData(company);
      if (research) {
        const r = research.research || research;
        const s = research.latest_score || research.score || null;
        setDetailResearch({ ...r, score: s, research_status: r.research_status || company.research_status });
      } else {
        setDetailResearch({ research_status: company.research_status || 'pending' });
      }
      setDetailLoading(false);
    }).catch(() => {
      if (!cancelled) setDetailLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedCompanyId]);

  const [researchingAll, setResearchingAll] = useState(false);
  const [scoringAll, setScoringAll] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    await load(1);
    setSyncing(false);
  };

  const handleResearchAll = async () => {
    const unresearched = companies.filter(c => !c.research_score);
    const ids = unresearched.map(c => c.id);
    if (ids.length === 0) {
      alert('All companies already have research.');
      return;
    }
    if (!confirm(`Queue research for ${ids.length} unresearched companies?`)) return;
    setResearchingAll(true);
    try {
      await api.researchCompanies(ids, { max: ids.length });
      alert(`Research queued for ${ids.length} companies. This may take a while.`);
    } catch (err) {
      alert('Failed to queue research: ' + (err.message || 'unknown'));
    } finally {
      setResearchingAll(false);
    }
  };

  const handleScoreAll = async () => {
    const ids = companies.map(c => c.id);
    if (ids.length === 0) return;
    if (!confirm(`Queue scoring for ${ids.length} companies?`)) return;
    setScoringAll(true);
    try {
      await api.scoreCompanies(ids, { max: ids.length });
      alert(`Scoring queued for ${ids.length} companies.`);
      await load(1);
    } catch (err) {
      alert('Failed to queue scoring: ' + (err.message || 'unknown'));
    } finally {
      setScoringAll(false);
    }
  };

  const toggleCheck = (companyId) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  };

  const toggleCheckAll = () => {
    if (checkedIds.size === filtered.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(filtered.map(c => c.id)));
    }
  };

  const assignBucketToChecked = async (bucketId) => {
    try {
      await api.fetch('/api/companies/bulk/bucket', {
        method: 'PUT',
        body: JSON.stringify({ company_ids: [...checkedIds], bucket: bucketId }),
      });
      setCheckedIds(new Set());
      await load(1);
    } catch (err) {
      console.error('Bulk bucket assign failed:', err);
    }
  };

  const toggleBucket = (bucketId) => {
    setSelectedBuckets(prev => {
      const next = new Set(prev);
      if (next.has(bucketId)) next.delete(bucketId);
      else next.add(bucketId);
      return next;
    });
  };

  const toggleIndustry = (industry) => {
    setSelectedIndustries(prev => {
      const next = new Set(prev);
      if (next.has(industry)) next.delete(industry);
      else next.add(industry);
      return next;
    });
  };

  // Unique industries for filter
  const industries = useMemo(() => {
    const set = new Set();
    companies.forEach(c => { if (c.industry) set.add(c.industry); });
    return [...set].sort();
  }, [companies]);

  const filtered = useMemo(() => {
    let list = companies;

    // Bucket filter
    if (selectedBuckets.size > 0) {
      if (selectedBuckets.has('__none__')) {
        list = list.filter(c => !c.bucket || selectedBuckets.has(c.bucket));
      } else {
        list = list.filter(c => selectedBuckets.has(c.bucket));
      }
    }

    // Industry filter
    if (selectedIndustries.size > 0) {
      list = list.filter(c => c.industry && selectedIndustries.has(c.industry));
    }

    // Owner filter — operates on the assignee_names string the backend
    // sends. "Mine" matches the current user's name OR email. "Unassigned"
    // matches null/empty. Specific user filters match that user's display
    // name. Cheap substring check is fine — assignee_names is already a
    // comma-separated list of "Name (email)" tokens.
    if (ownerFilter !== 'all') {
      const meName  = (auth?.user?.name || '').toLowerCase().trim();
      const meEmail = (auth?.user?.email || '').toLowerCase().trim();
      if (ownerFilter === 'unassigned') {
        list = list.filter(c => !c.assignee_names || !c.assignee_names.trim());
      } else if (ownerFilter === 'mine') {
        list = list.filter(c => {
          const a = (c.assignee_names || '').toLowerCase();
          if (!a) return false;
          return (meName && a.includes(meName)) || (meEmail && a.includes(meEmail));
        });
      } else {
        // Specific user — match by their display name or email.
        const u = users.find(x => x.id === ownerFilter);
        if (u) {
          const n = (u.name || '').toLowerCase();
          const e = (u.email || '').toLowerCase();
          list = list.filter(c => {
            const a = (c.assignee_names || '').toLowerCase();
            return (n && a.includes(n)) || (e && a.includes(e));
          });
        }
      }
    }

    // Search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.industry || '').toLowerCase().includes(q) ||
        (c.intro_names || '').toLowerCase().includes(q)
      );
    }

    // Sort
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'warmth': return (b.warmth || 0) - (a.warmth || 0);
        case 'icp': return (b.research_score || 0) - (a.research_score || 0);
        case 'connection':
          // Companies with a direct (intro) connection first; ties broken
          // by warmth so the warmest connected companies float up.
          return (b.connection_count || 0) - (a.connection_count || 0)
              || (b.warmth || 0) - (a.warmth || 0);
        case 'contacts': return (b.contact_count || 0) - (a.contact_count || 0);
        case 'name': return (a.name || '').localeCompare(b.name || '');
        default: return (b.warmth || 0) - (a.warmth || 0);
      }
    });
    return sorted;
  }, [companies, selectedBuckets, selectedIndustries, search, sortBy, ownerFilter, users, auth?.user?.name, auth?.user?.email]);

  const stats = useMemo(() => {
    const scored = companies.filter(c => c.research_score).length;
    return { scored, total: companies.length };
  }, [companies]);

  const maxWarmth = useMemo(() => {
    return Math.max(1, ...companies.map(c => c.warmth || 0));
  }, [companies]);

  const handleResearchSingle = async (companyId, domain) => {
    const res = await api.researchSingleCompany(companyId, { domain });
    setSelectedCompanyId(null);
    setTimeout(() => setSelectedCompanyId(companyId), 50);
    return res;
  };

  const handleRescore = async (companyId) => {
    await api.scoreCompanies([companyId], { force: true });
    setSelectedCompanyId(null);
    setTimeout(() => setSelectedCompanyId(companyId), 50);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-500 text-sm">Loading companies...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex overflow-hidden">
      {/* Main list */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-800 bg-slate-900">
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && checkedIds.size === filtered.length}
                  ref={el => { if (el) el.indeterminate = checkedIds.size > 0 && checkedIds.size < filtered.length; }}
                  onChange={toggleCheckAll}
                  className="w-3.5 h-3.5 accent-slate-400 cursor-pointer"
                  title="Select all"
                />
                <div>
                  <h2 className="text-lg font-semibold text-slate-100 tracking-tight">Companies</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {filtered.length} shown of {stats.total} &middot; {stats.scored} scored
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleResearchAll}
                  disabled={researchingAll}
                  className="px-3 py-1.5 text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-900/30 rounded-md transition-colors disabled:opacity-50"
                >
                  {researchingAll ? 'Queuing...' : 'Research'}
                </button>
                <button
                  onClick={handleScoreAll}
                  disabled={scoringAll}
                  className="px-3 py-1.5 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30 rounded-md transition-colors disabled:opacity-50"
                >
                  {scoringAll ? 'Queuing...' : 'Score'}
                </button>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-md transition-colors disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Refresh'}
                </button>
              </div>
            </div>

            {/* Search + Sort row */}
            <div className="flex items-center gap-3 mb-3">
              <input
                type="text"
                placeholder="Search companies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 max-w-xs bg-slate-800/60 border border-slate-700/50 rounded-md px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600 outline-none focus:border-slate-600 transition-colors"
              />
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <span>Sort:</span>
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setSortBy(opt.id)}
                    className={`px-2 py-1 rounded transition-colors ${
                      sortBy === opt.id
                        ? 'bg-slate-700 text-slate-200'
                        : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Bucket filter */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              <ChipTooltip text="Companies not yet sorted into a bucket — your default prospecting queue.">
                <button
                  onClick={() => toggleBucket('__none__')}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors border ${
                    selectedBuckets.has('__none__')
                      ? 'bg-slate-600 text-slate-100 border-transparent'
                      : 'bg-transparent text-slate-500 border-slate-700/50 hover:text-slate-300 hover:border-slate-600'
                  }`}
                >
                  📋 Prospects
                </button>
              </ChipTooltip>
              {bucketDefs.map(b => {
                const active = selectedBuckets.has(b.id);
                return (
                  <ChipTooltip key={b.id} text={b.description}>
                    <button
                      onClick={() => toggleBucket(b.id)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors border ${
                        active
                          ? 'text-white border-transparent'
                          : 'bg-transparent text-slate-500 border-slate-700/50 hover:text-slate-300 hover:border-slate-600'
                      }`}
                      style={active ? { backgroundColor: b.color || '#6366f1', borderColor: 'transparent' } : {}}
                    >
                      {b.emoji ? `${b.emoji} ` : ''}{b.label}
                    </button>
                  </ChipTooltip>
                );
              })}
              {selectedBuckets.size > 0 && (
                <button
                  onClick={() => setSelectedBuckets(new Set())}
                  className="px-2 py-1 text-[11px] text-slate-600 hover:text-slate-400 transition-colors"
                >
                  Clear
                </button>
              )}

              <span className="text-slate-800 mx-1">|</span>

              {/* Industry filter toggle */}
              <button
                onClick={() => setShowIndustryFilter(!showIndustryFilter)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors border ${
                  selectedIndustries.size > 0
                    ? 'bg-slate-600 text-slate-100 border-transparent'
                    : 'bg-transparent text-slate-500 border-slate-700/50 hover:text-slate-300 hover:border-slate-600'
                }`}
              >
                Industry {selectedIndustries.size > 0 ? `(${selectedIndustries.size})` : ''}
                <span className="ml-1 text-[9px]">{showIndustryFilter ? '▲' : '▼'}</span>
              </button>
              {selectedIndustries.size > 0 && (
                <button
                  onClick={() => setSelectedIndustries(new Set())}
                  className="px-2 py-1 text-[11px] text-slate-600 hover:text-slate-400 transition-colors"
                >
                  Clear industries
                </button>
              )}

              {/* Owner filter — Mine / Unassigned / per-rep / All. Drives
                  multi-person distribution: each rep clicks "Mine" to see
                  only their queue, "Unassigned" to claim work. */}
              <span className="text-slate-700 mx-1">|</span>
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="bg-slate-800/40 text-slate-300 text-[11px] rounded-md border border-slate-700/50 px-2 py-1 hover:text-slate-100 outline-none"
                title="Filter companies by assignee"
              >
                <option value="all">All assignees</option>
                <option value="mine">Mine</option>
                <option value="unassigned">Unassigned</option>
                <option disabled>──────────</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>

            {/* Industry dropdown */}
            {showIndustryFilter && (
              <div className="flex flex-wrap gap-1 py-2 px-1 bg-slate-800/40 rounded-md max-h-32 overflow-y-auto scrollable-content">
                {industries.map(ind => {
                  const active = selectedIndustries.has(ind);
                  return (
                    <button
                      key={ind}
                      onClick={() => toggleIndustry(ind)}
                      className={`px-2 py-0.5 rounded text-[10px] transition-colors ${
                        active
                          ? 'bg-slate-600 text-slate-100'
                          : 'text-slate-500 hover:text-slate-300 bg-slate-800/60'
                      }`}
                    >
                      {ind}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Bulk action bar */}
        {checkedIds.size > 0 && (
          <div className="flex-shrink-0 px-6 py-2.5 bg-slate-800/80 border-b border-slate-700/50 flex items-center gap-3">
            <span className="text-xs text-slate-300 font-medium">{checkedIds.size} selected</span>
            <span className="text-slate-700">|</span>
            <span className="text-[11px] text-slate-500">Assign bucket:</span>
            {bucketDefs.map(b => (
              <button
                key={b.id}
                onClick={() => assignBucketToChecked(b.id)}
                className="px-2 py-0.5 rounded text-[11px] font-medium text-white transition-colors hover:opacity-80"
                style={{ backgroundColor: b.color || '#6366f1' }}
              >
                {b.emoji ? `${b.emoji} ` : ''}{b.label}
              </button>
            ))}
            <button
              onClick={() => assignBucketToChecked(null)}
              className="px-2 py-0.5 rounded text-[11px] text-slate-400 bg-slate-700 hover:bg-slate-600 transition-colors"
            >
              Clear bucket
            </button>

            <span className="text-slate-700 mx-1">|</span>
            <span className="text-[11px] text-slate-500">Assign to:</span>
            <select
              disabled={assignSaving}
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (v === '__me__') assignOwnerToChecked(auth?.user?.id);
                else if (v === '__clear__') assignOwnerToChecked(null);
                else assignOwnerToChecked(v);
                e.target.value = '';
              }}
              className="bg-slate-700 text-slate-100 text-[11px] rounded px-2 py-0.5 outline-none"
            >
              <option value="">— pick rep —</option>
              {auth?.user?.id && <option value="__me__">Me ({auth.user.name || auth.user.email})</option>}
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
              <option disabled>──────────</option>
              <option value="__clear__">Unassign all</option>
            </select>

            <button
              onClick={() => setCheckedIds(new Set())}
              className="ml-auto text-[11px] text-slate-500 hover:text-slate-300 transition-colors"
            >
              Deselect all
            </button>
          </div>
        )}

        {/* Company list */}
        <div className="flex-1 overflow-y-auto scrollable-content">
          {loadError ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-400 gap-2">
              <span className="text-sm">Failed to load companies: {loadError}</span>
              <button onClick={() => { setLoadError(''); load(1); }} className="text-xs text-slate-400 hover:text-slate-200 underline">Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-600 gap-2">
              <span className="text-sm">
                {search ? 'No companies match your search' : selectedBuckets.size > 0 || selectedIndustries.size > 0 ? 'No companies match filters' : 'No companies yet'}
              </span>
            </div>
          ) : (
            <div>
              {filtered.map(company => {
                const isSelected = selectedCompanyId === company.id;
                const barWidth = maxWarmth > 0 ? Math.max(2, (company.warmth / maxWarmth) * 100) : 0;
                const bucketDef = company.bucket ? bucketDefs.find(b => b.id === company.bucket) : null;

                return (
                  <div
                    key={company.id}
                    className={`w-full text-left px-6 py-4 border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors flex items-center gap-3 ${
                      isSelected ? 'bg-slate-800/40 border-l-2 border-l-slate-400' : 'border-l-2 border-l-transparent'
                    }`}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={checkedIds.has(company.id)}
                      onChange={(e) => { e.stopPropagation(); toggleCheck(company.id); }}
                      className="w-3.5 h-3.5 accent-slate-400 flex-shrink-0 cursor-pointer"
                    />
                    <button
                      onClick={() => setSelectedCompanyId(isSelected ? null : company.id)}
                      className="flex-1 text-left min-w-0"
                    >
                    <div className="flex items-center gap-4">
                      {/* Company logo or initials */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center overflow-hidden">
                        {company.website ? (
                          <img
                            src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(company.website.replace(/^https?:\/\//, '').split('/')[0])}&sz=32`}
                            alt=""
                            className="w-6 h-6"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <span
                          className="text-sm font-semibold text-slate-400"
                          style={company.website ? { display: 'none' } : {}}
                        >
                          {(company.name || '??').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                        </span>
                      </div>

                      {/* Company info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="text-base font-medium text-slate-100 truncate">
                            {company.name}
                          </span>
                          {bucketDef && (
                            <span
                              className="text-[11px] px-2 py-0.5 rounded font-medium text-white flex-shrink-0"
                              style={{ backgroundColor: bucketDef.color || '#6366f1' }}
                            >
                              {bucketDef.emoji ? `${bucketDef.emoji} ` : ''}{bucketDef.label}
                            </span>
                          )}
                          {company.research_score && (
                            <span className={`text-[11px] font-medium tabular-nums flex-shrink-0 ${
                              company.research_score >= 80 ? 'text-indigo-400' :
                              company.research_score >= 60 ? 'text-slate-300' :
                              'text-slate-500'
                            }`}>
                              ICP {company.research_score}
                            </span>
                          )}
                          {company.warmth > 0 && (
                            <span className="relative flex-shrink-0">
                              <span
                                className="text-[11px] font-medium tabular-nums text-slate-500 cursor-pointer hover:text-slate-300 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setWarmthPopoverId(warmthPopoverId === company.id ? null : company.id);
                                }}
                              >
                                W {company.warmth}
                              </span>
                              {warmthPopoverId === company.id && (
                                <div
                                  className="absolute left-0 top-6 z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-xl px-3 py-2.5 text-[11px] text-slate-300 whitespace-pre leading-relaxed min-w-[180px]"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {warmthBreakdown(company)}
                                </div>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Compact meta line: industry + signal summary + assignee */}
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
                          {company.industry && <span>{company.industry}</span>}
                          {company.industry && (company.connection_count > 0 || company.follower_count > 0 || company.engagement_count > 0) && (
                            <span className="text-slate-700">&middot;</span>
                          )}
                          {company.connection_count > 0 && (
                            <span title={company.intro_names ? `Connected via ${company.intro_names}` : ''}>
                              {company.connection_count} conn
                            </span>
                          )}
                          {company.follower_count > 0 && (
                            <span>{company.follower_count} follow</span>
                          )}
                          {company.engagement_count > 0 && (
                            <span>{company.engagement_count} engage</span>
                          )}
                          {company.assignee_names ? (
                            <>
                              <span className="text-slate-700">&middot;</span>
                              <span className="inline-flex items-center gap-1 text-indigo-300/80" title={`Assigned to ${company.assignee_names}`}>
                                <svg className="w-3 h-3 inline-block" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                </svg>
                                {company.assignee_names}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="text-slate-700">&middot;</span>
                              <span className="text-slate-600 italic">unassigned</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Connection column — WHO can make a direct intro
                          (from lead_intros, never followers). N/A when
                          nobody on the team is connected. Sortable via the
                          "Connection" sort option. */}
                      <div className="w-32 flex-shrink-0 pt-0.5 text-right">
                        {company.connection_count > 0 && company.intro_names ? (
                          <span
                            className="text-xs text-emerald-300/90 truncate block"
                            title={`Direct connection via ${company.intro_names}`}
                          >
                            {company.intro_names}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-600 block">N/A</span>
                        )}
                        <div className="text-[10px] text-slate-600 mt-0.5">connection</div>
                      </div>

                      {/* Right column — contacts + bar */}
                      <div className="w-24 flex-shrink-0 pt-0.5 text-right">
                        <div className="text-xs text-slate-400 tabular-nums">
                          {company.contact_count} contact{company.contact_count !== 1 ? 's' : ''}
                        </div>
                        {company.warmth > 0 && (
                          <div className="h-1 bg-slate-800 rounded-full overflow-hidden mt-2">
                            <div
                              className="h-full rounded-full bg-slate-500"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Side panel — full CompanyDetailPanel */}
      {selectedCompanyId && (
        detailLoading ? (
          <div className="w-96 bg-slate-800 border-l border-slate-700 flex items-center justify-center flex-shrink-0">
            <span className="text-slate-500 text-sm">Loading...</span>
          </div>
        ) : detailData ? (
          <CompanyDetailPanel
            company={detailData}
            research={detailResearch}
            onClose={() => setSelectedCompanyId(null)}
            onResearchSingle={handleResearchSingle}
            onRescore={handleRescore}
            onChange={() => { load(); setSelectedCompanyId(null); setTimeout(() => setSelectedCompanyId(detailData.id), 50); }}
          />
        ) : null
      )}
    </div>
  );
}
